import { useState, useContext, useEffect } from 'react';
import {
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    Box,
    Typography,
    Chip,
    IconButton,
    InputBase,
    Paper,
    Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { AppContext, SttFormSearch, SttPagination, SttNoResults } from '../all';
import { checkOffTag, fullName, formatPriceByLocale } from '../../lib/utils';
import UserCreationDialog from '../../components/dialogs/UserCreationDialog';

type User = {
    id: number;
    name: string;
    surname1?: string;
    surname2?: string;
    login?: string;
};

type Mship = {
    id: number;
    fullName: string;
    user: User;
    img?: { url: string };
    amount?: number;
    debt?: number;
    amountShape?: {
        currency: string;
        locale: string;
    };
};

type Props = {
    grid?: boolean;
    caption?: string;
    name?: string;
    user?: User;
    onChange?: (event: { name: string | undefined; value: unknown }) => void;
    onSelect?: (user: User | Mship | null) => void;
    controlDisabled?: boolean;
    returnMship?: boolean;
    error?: boolean;
    helperText?: string;
    [key: string]: unknown;
};

export function SttSelectUser({
    grid = false,
    caption,
    _name,
    user,
    onChange,
    onSelect,
    controlDisabled,
    returnMship = false,
    error,
    helperText,
    ...rest
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [dialogOpened, setDialogOpened] = useState(false);
    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
    const [mships, setMships] = useState<{
        count: number;
        rows: Mship[];
        pages?: number;
    }>();
    const [keywordSearch, setKewordSearch] = useState('');
    const [page, setPage] = useState(1);

    /**
     * Load mships
     */
    const loadMships = (p: Record<string, unknown> = { page: 1 }) => {
        const currPage = p.page !== undefined ? p.page : page;
        cxt?.api('GET', `/scs/${cxt.sc?.id}/mships`, {
            params: {
                page: currPage,
                rows: 6,
                keyword: keywordSearch,
            },
            success: (r: { count: number; rows: Mship[]; pages: number }) => {
                setMships(r);
                setPage(currPage as number);
            },
        });
    };

    useEffect(() => {
        loadMships();
    }, []);

    /**
     * Click search
     */
    const handleClickSearch = () => {
        if (!mships) {
        }
        setDialogOpened(true);
    };

    /**
     * Select
     */
    const onSelectOnList = (mship: Mship) => {
        setDialogOpened(false);
        if (onSelect && mship.user && !returnMship) {
            onSelect(mship.user);
        } else if (onSelect && mship.user && returnMship) {
            onSelect(mship);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            loadMships({ page: 1 });
        }
    };

    // -----| Render |-----
    const content = (
        <>
            <SttFormSearch
                disabled
                caption={caption}
                defVal={fullName(user) || ''}
                onClean={
                    controlDisabled
                        ? undefined
                        : () => onSelect && onSelect(null)
                }
                onChange={onChange as any}
                onClickSearch={controlDisabled ? undefined : handleClickSearch}
                error={error}
                helperText={helperText}
            />

            <Dialog
                fullWidth
                maxWidth="sm"
                onClose={() => setDialogOpened(false)}
                open={dialogOpened}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('labels.users')}
                    </Typography>
                    <IconButton
                        onClick={() => setDialogOpened(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Paper
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                            elevation={0}
                        >
                            <SearchIcon
                                sx={{ color: 'text.secondary', mr: 1 }}
                            />
                            <InputBase
                                placeholder={t('common.search')}
                                value={keywordSearch}
                                onChange={(e: any) =>
                                    setKewordSearch(e.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                sx={{ flex: 1 }}
                                fullWidth
                            />
                        </Paper>
                        <Button
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={() => setCreateUserDialogOpen(true)}
                        >
                            {t('user.createUser')}
                        </Button>
                    </Box>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    <List sx={{ py: 0 }}>
                        {mships?.rows.map((mship: any, index: any) => {
                            const isUnsubscribed =
                                mship.user && checkOffTag(mship.user.login);

                            return (
                                <ListItemButton
                                    key={mship.id}
                                    onClick={() => onSelectOnList(mship)}
                                    sx={{
                                        py: 2,
                                        px: 3,
                                        borderBottom:
                                            index < mships.rows.length - 1
                                                ? '1px solid'
                                                : 'none',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            bgcolor: 'primary.50',
                                        },
                                        opacity: isUnsubscribed ? 0.6 : 1,
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            alt={mship.fullName}
                                            src={
                                                mship.img
                                                    ? mship.img.url
                                                    : undefined
                                            }
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: 'grey.300',
                                            }}
                                        />
                                    </ListItemAvatar>

                                    <Box sx={{ ml: 1, flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={500}
                                            sx={{
                                                color: isUnsubscribed
                                                    ? 'text.secondary'
                                                    : 'text.primary',
                                            }}
                                        >
                                            {mship.fullName}
                                        </Typography>

                                        {mship.user?.login && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                @{mship.user.login}
                                            </Typography>
                                        )}

                                        {isUnsubscribed ? (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {t('user.userCancelled')}
                                            </Typography>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    mt: 0.5,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Chip
                                                    icon={
                                                        <AccountBalanceWalletIcon
                                                            sx={{
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    }
                                                    label={`${t('payment.purse')}: ${formatPriceByLocale(
                                                        mship.amount || 0,
                                                        mship?.amountShape
                                                            ?.currency,
                                                        mship?.amountShape
                                                            ?.locale,
                                                    )}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'success.50',
                                                        color: 'success.700',
                                                        fontWeight: 500,
                                                        '& .MuiChip-icon': {
                                                            color: 'success.600',
                                                        },
                                                    }}
                                                />
                                                {mship.debt &&
                                                    mship.debt > 0 && (
                                                        <Chip
                                                            icon={
                                                                <WarningAmberIcon
                                                                    sx={{
                                                                        fontSize: 16,
                                                                    }}
                                                                />
                                                            }
                                                            label={`${t('payment.debt')}: ${formatPriceByLocale(
                                                                mship.debt,
                                                                mship
                                                                    ?.amountShape
                                                                    ?.currency,
                                                                mship
                                                                    ?.amountShape
                                                                    ?.locale,
                                                            )}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor:
                                                                    'error.50',
                                                                color: 'error.700',
                                                                fontWeight: 500,
                                                                '& .MuiChip-icon':
                                                                    {
                                                                        color: 'error.600',
                                                                    },
                                                            }}
                                                        />
                                                    )}
                                            </Box>
                                        )}
                                    </Box>
                                </ListItemButton>
                            );
                        })}

                        {mships && mships.count === 0 && <SttNoResults />}
                    </List>

                    {mships && mships.count > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                            }}
                        >
                            <SttPagination
                                page={page}
                                pages={mships.pages || 0}
                                count={mships.count}
                                onChangePage={(page) => loadMships({ page })}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <UserCreationDialog
                open={createUserDialogOpen}
                onClose={() => setCreateUserDialogOpen(false)}
                onCreate={() => {
                    setCreateUserDialogOpen(false);
                    loadMships({ page: 1 });
                }}
            />
        </>
    );
    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
